import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  registerables,
} from 'chart.js';
import { Dayjs } from 'dayjs';
import DashboardBigCard from '../dashboardCards/dashboardBigCard';
import { PROFITABLE_UNPROFITABLE_GRAPH } from '../../../constants/pageIdentifiers';
import { useDashboardFilterDateStore } from '../../../store/dateRangeStore';
import { TrackingSourceInterface } from '../../../interfaces/trackingSource.interface';
import TrackingSourceSelect from '../../dropdownSelects/trackingSourceSelect';
import SecondaryButton from '../../buttons/secondaryButton';
import { Colors } from '../../../constants/constants';
import CustomDateRangePickerGraph from '../../../partials/customDateRangePicker/customDateRnagePIckerGraph';
import { useGetProfitableUnprofitableGraph } from '../../../apiHooks/queries/graphs/useGetProfitableUnprofitableGraph';
import ThreeDots from '../../../assets/icons/threeDots.svg';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ...registerables);

const colors = ['#94BFA7', '#DB3A34'];

interface ProfitablePercentageData {
  data: {
    labels: string[];
    dataSets: Record<string, number[]>;
  };
}

interface Visibility {
  profitablePercentage: boolean;
  unprofitablePercentage: boolean;
}

const buildData = (
  profitablePercentageData: ProfitablePercentageData | undefined,
  t: (_key: string) => string,
  visibility: Visibility,
) => ({
  labels: profitablePercentageData?.data?.labels.map((label: string) => label.slice(0).toUpperCase()) || [],
  datasets: Object.entries(profitablePercentageData?.data?.dataSets || {}).map(([key, value], index) => ({
    label: t(key),
    data: value.map((v: number) => (visibility[key as keyof Visibility] ? v : null)),
    fill: false,
    backgroundColor: '#ffffff',
    borderColor: colors[index % colors.length],
    borderRadius: 5,
    borderWidth: 2,
  })),
});

interface ProfitableGraphProps {
  selectedTracking : TrackingSourceInterface | null;
}
const options: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        font: {
          family: 'Poppins',
        },
      },
    },
    y: {
      min: 0,
      max: 100,
      ticks: {
        callback(value) {
          return `${value}%`;
        },
        font: {
          family: 'Poppins',
        },
      },
      border: {
        display: false,
        dash: [2, 2],
        color: '#e0e0e0',
      },
    },
  },
  layout: {
    padding: {
      top: 20,
      left: 20,
      right: 20,
      bottom: 20,
    },
  },
};

const initialVisibility: Visibility = {
  profitablePercentage: true,
  unprofitablePercentage: true,
};

const ProfitableUnprofitableGraph: FC<ProfitableGraphProps > = (props) => {
  const { t } = useTranslation();
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [interval, setIntervalState] = useState<'day' | 'week' | 'month'>('day');
  const [selectedTrackingSource, setSelectedTrackingSource] = useState<TrackingSourceInterface | null>(null);
  const trackingSourceId = selectedTrackingSource ? Number(selectedTrackingSource.id) : 0;
  const [isOpen, setIsOpen] = useState(false);
  const globalRange = useDashboardFilterDateStore().dashboardFilterDateRange;
  const [localRange, setLocalRange] = useState<[Dayjs | null, Dayjs | null]>(globalRange);
  const dropdownRef = useRef(null);
  const { data: profitablePercentageData, isError, isLoading } = useGetProfitableUnprofitableGraph(
    PROFITABLE_UNPROFITABLE_GRAPH,
    localRange[0],
    localRange[1],
    interval,
    trackingSourceId,
  );

  const [visibility, setVisibility] = useState<Visibility>(initialVisibility);
  const toggleDataset = (datasetLabel: keyof Visibility) => {
    setVisibility((prev) => ({
      ...prev,
      [datasetLabel]: !prev[datasetLabel],
    }));
  };
  useEffect(() => {
    setLocalRange(globalRange);
  }, [globalRange]);

  return (
    <DashboardBigCard
      info="The percentage of your total ads generating profit vs. those operating at a loss."
      title="Ad Performance"
      isLoading={isLoading}
      isError={isError}
      headerRight={(
        <div className="mx-4 flex w-full max-w-[calc(100%-400px)] items-center justify-between gap-2">
          <div className="items-end ">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="flex h-[32px] w-[32px] items-center rounded-md border border-[#CEDADF] bg-white p-2 text-sm
               shadow-sm"
            >
              <img src={ThreeDots} className="h-[20px] w-[20px]" alt="thredots" />
            </button>

            {isOpen && (
              <div
                ref={dropdownRef}
                className=" absolute right-8 z-50  w-[260px] rounded-lg border bg-white  shadow-lg"
              >
                <div className=" border-b px-3 pb-2 pt-3 text-lg text-primary">
                  Filter Options
                </div>
                <div className="mb-4 h-[35px] w-full p-2">
                  <TrackingSourceSelect
                    size="small"
                    label="Tracking Source"
                    placeholder="Tracking Source"
                    fullWidth
                    data={selectedTrackingSource ?? undefined}
                    setSelectedTrackingSource={(value) => {
                      setSelectedTrackingSource(value);
                    }}
                  />
                </div>
                <div className="w-full p-2">
                  <SecondaryButton
                    type="button"
                    style={{
                      paddingInline: '10px',
                      height: '40px',
                      width: '240px',
                      backgroundColor: 'white',
                      border: `1px solid ${isCalendarOpen ? Colors.primary : '#eaeaea'}`,
                    }}
                    onMouseDown={() => setIsCalendarOpen(true)}
                    dataCy="dashboard-actions-calendar-toggle"
                  >
                    <CustomDateRangePickerGraph
                      filterRange={localRange}
                      setFilterRange={setLocalRange}
                      isCalendarOpen={isCalendarOpen}
                      setIsCalendarOpen={setIsCalendarOpen}
                      iconButton
                      showFilterDates
                    />
                  </SecondaryButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    >
      {profitablePercentageData?.data?.dataSets && !isError && !isLoading && (
        <div className="flex h-full w-full flex-col">
          <div className="mb-4 flex w-full shrink-0 justify-between gap-2">
            {Object.keys(initialVisibility)
              .map((key) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => toggleDataset(key as keyof Visibility)}
                  className={`flex h-[35px] w-full min-w-0 items-center gap-1 rounded-xl 
                border py-2 pl-4 pr-2 text-[10px] outline-none focus:outline-none ${visibility[key as keyof Visibility]
                    ? 'bg-white text-primary' : 'text-black'}`}
                >
                  <div
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{
                      backgroundColor: visibility[key as keyof Visibility]
                        ? colors[Object.keys(initialVisibility)
                          .indexOf(key) % colors.length]
                        : 'gray',
                    }}
                  />
                  <span className="truncate">{t(key)}</span>
                </button>
              ))}
          </div>
          <div className="relative min-h-0 w-full flex-1">
            <Line options={options} data={buildData(profitablePercentageData, t, visibility)} />
          </div>
        </div>
      )}
    </DashboardBigCard>
  );
};
export default ProfitableUnprofitableGraph;
